import { ResultModal } from "../../EventSales/eventSalesShared";
import { CmpEventLibraryKindEnum, CmpEventTicketAudienceEnum } from "../../../../../graphql/types";
import EventLibrarySelect, { deletedMessage as deletedEntityMessage } from "./EventLibrarySelect";
import { isMemberOnlyTicketName } from "../../eventBackendMappers";
import React, { useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { EventTicketType } from "../../eventMockData";
import {
  FieldLabel,
  WizardActionButton,
  WizardDialogHeader,
  WizardStepProps,
  wizardCurrencyAdornmentClassName,
  wizardId,
  wizardTextFieldSx,
} from "./wizardShared";
import { useWizardDialogUnsavedGuard } from "./wizardDialogUnsavedGuard";
import { EventReorderHandle, eventReorderTableRowProps, useEventRowReorder } from "./useEventRowReorder";
import { ConfirmDialog, getConfiguredInvoiceTaxRatePercentage } from "../../EventSales/eventSalesShared";
import { useCmpGlobalSettings } from "../../../../../hooks/useCmpSettingsSection";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import CalendarDateInput from "../../../MyChamber/ChamberSettings/common/CalendarDateInput";
import { SectionShell } from "../../../../common/dashboard/SectionShell";
import { CurrencyInput } from "../../../../common/dashboard/CurrencyInput";
import { enterFieldMessage } from "../../../../../utils/fieldMessages";

// New tickets start visible on the registration form. Claude Design (8)
// removed the "Members only" box, so members-only comes from the ticket name
// when the ticket is saved (saveDraft, decision Q13).
const blankTicket = (): EventTicketType => ({
  id: wizardId("ticket"),
  name: "",
  description: "",
  price: "",
  attendeesPerTicket: "1",
  memberOnly: false,
  code: "",
  availabilityLimit: "",
  salesStartDate: "",
  salesEndDate: "",
  visibleOnRegistrationForm: true,
});

// CES:753-760 (EV15-32): "May 15, 2026 \u2013 June 15, 2026", full month; one
// date alone when only one is set.
const salesDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

const salesPeriodLabel = (ticket: EventTicketType) => {
  if (ticket.salesStartDate || ticket.salesEndDate) {
    const formatDate = (value?: string) => {
      if (!value) {
        return "—";
      }
      const [year, month, day] = value.split("-").map(Number);
      if (!year || !month || !day) {
        return value;
      }
      return salesDateFormatter.format(new Date(year, month - 1, day));
    };
    if (!ticket.salesStartDate || !ticket.salesEndDate) {
      return formatDate(ticket.salesStartDate || ticket.salesEndDate);
    }
    return `${formatDate(ticket.salesStartDate)} \u2013 ${formatDate(ticket.salesEndDate)}`;
  }
  return "—";
};

const ticketTableHeadCellClassName =
  "!text-[11px] !font-semibold !uppercase !tracking-wide !text-[#0A0A0A]";

const deletedTicketMessage = (name: string) => deletedEntityMessage("Ticket Type", name || "this ticket type");

// Case and spacing edits are not a rename (decision Q13 keeps saved audiences).
const sameTicketName = (left: string, right: string) =>
  left.trim().replace(/\s+/g, " ").toLowerCase() === right.trim().replace(/\s+/g, " ").toLowerCase();

// Read-only $-display for the ticket table PRICE column: ticket.price is now
// the canonical decimal-dollars string ("85.00"), so the spec's "$85.00"
// column re-adds the "$" at render time (same shape as Step4's formatDollars).
const formatDollars = (value?: string): string => {
  const numeric = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return `$${(Number.isFinite(numeric) ? numeric : 0).toFixed(2)}`;
};

const Step3TicketSetup = ({
  chamberId,
  event,
  setEvent,
  errors,
  onDialogSaved,
}: WizardStepProps): React.ReactElement => {
  const { data: settingsData, error: settingsError } = useCmpGlobalSettings(chamberId);
  const noun = useCmpOrganizationNoun();
  const ticketTaxSettings = settingsData?.getChamberMemberProSettings.paymentsAndTaxes?.invoiceTaxSettings?.eventTicketsInvoices;
  const taxableLabel = settingsError || !ticketTaxSettings
    ? "Unavailable"
    : getConfiguredInvoiceTaxRatePercentage(ticketTaxSettings) > 0 ? "Yes" : "No";
  const update = <K extends keyof typeof event>(key: K, value: (typeof event)[K]) =>
    setEvent((prev) => ({ ...prev, [key]: value }));
  // Create Event Screen.dc.html:781-786, 2452, 2493: the 24/48/72 radios stay
  // visible, dimmed and disabled while pre-check-in is off, with none
  // selected. On with no window is blocked on save (decision Q17, EventWizard).
  // A missing flag keeps the backend meaning (on).
  const preCheckInOn = event.preCheckInEnabled ?? true;
  const preCheckInWindowMissing = preCheckInOn && ![24, 48, 72].includes(Number(event.preCheckInHours));

  const [draft, setDraft] = useState<EventTicketType | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EventTicketType | null>(null);
  // Rows reorder by their "⋮⋮" handle (EVT-TICKET-001, CES:758, :2610-2620).
  const reorderTicketRow = useEventRowReorder(event.tickets, (tickets) => update("tickets", tickets));

  const [showValidation, setShowValidation] = useState(false);
  const openDraft = (nextDraft: EventTicketType, nextIsNew: boolean) => {
    setDraft(nextDraft);
    setIsNew(nextIsNew);
    setShowValidation(false);
    unsavedGuard.snapshot(nextDraft);
  };
  const openAdd = () => openDraft(blankTicket(), true);
  const openEdit = (ticket: EventTicketType) => openDraft({ ...ticket }, false);
  const patchDraft = (patch: Partial<EventTicketType>) =>
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const draftLimit = draft ? Number(draft.availabilityLimit) || 0 : 0;
  const setDraftLimit = (nextLimit: number) =>
    patchDraft({ availabilityLimit: nextLimit > 0 ? String(nextLimit) : "" });
  const draftAttendeesPerTicket = draft
    ? Math.max(1, Math.floor(Number(draft.attendeesPerTicket) || 1))
    : 1;

  // Design validates on Save click (saveTicketModal): only the name is
  // required (Ticket Code is optional); seats must stay a whole number
  // of at least 1.
  const draftNameMissing = !draft?.name.trim();
  const canSaveDraft = Boolean(
    draft &&
      !draftNameMissing &&
      Number.isInteger(Number(draft.attendeesPerTicket || "1")) &&
      Number(draft.attendeesPerTicket || "1") >= 1
  );

  const [saveResult, setSaveResult] = useState<string | null>(null);
  const [deletedMessage, setDeletedMessage] = useState<{ message: string; highlights: string[] } | null>(null);

  const saveDraft = () => {
    if (!draft) {
      return;
    }
    if (!canSaveDraft) {
      setShowValidation(true);
      return;
    }
    // Decision Q13 + Sept 28 follow-up: a ticket that is not saved to the event
    // yet is members-only when its name says so (isMemberOnlyTicketName). A
    // saved ticket keeps its audience when renamed, because Step 3 no longer
    // shows the audience and a rename must not silently open a members-only
    // ticket to everyone. Oct 6 (APBOT Coffee Connect): a saved ticket renamed
    // from a non-member name to a member name becomes members-only.
    // Sponsor-included tickets never change.
    const savedTicket = isNew ? undefined : event.tickets.find((item) => item.id === draft.id);
    const renamed = !savedTicket || !sameTicketName(savedTicket.name, draft.name);
    const audienceFollowsName = renamed && draft.audience !== CmpEventTicketAudienceEnum.SponsorIncluded;
    const memberOnly = !audienceFollowsName
      ? draft.memberOnly
      : !savedTicket?.persisted
        ? isMemberOnlyTicketName(draft.name)
        : draft.memberOnly ||
          (!isMemberOnlyTicketName(savedTicket.name) && isMemberOnlyTicketName(draft.name));
    // "For free tickets leave price set at $0.00" — an untouched price field
    // saves as $0.00 rather than an empty string. POL-005: prices are now the
    // canonical decimal-dollars shape ("0.00", no "$"); the "$" is re-added at
    // display time only.
    const ticket = {
      ...draft,
      memberOnly,
      price: draft.price.trim() || "0.00",
      attendeesPerTicket: String(draftAttendeesPerTicket),
    };
    update(
      "tickets",
      isNew
        ? [...event.tickets, ticket]
        : event.tickets.map((item) => (item.id === ticket.id ? ticket : item))
    );
    setDraft(null);
    if (onDialogSaved) onDialogSaved();
    else setSaveResult(isNew ? "Ticket type added." : "Ticket type updated.");
  };
  const unsavedGuard = useWizardDialogUnsavedGuard({ draft, onSave: saveDraft, onLeave: () => setDraft(null) });

  const confirmDelete = () => {
    if (!pendingDelete) {
      return;
    }
    update(
      "tickets",
      pendingDelete.persisted
        ? event.tickets.map((item) =>
            item.id === pendingDelete.id
              ? { ...item, visibleOnRegistrationForm: false }
              : item
          )
        : event.tickets.filter((item) => item.id !== pendingDelete.id)
    );
    setDeletedMessage(deletedTicketMessage(pendingDelete.name));
    setPendingDelete(null);
  };

  return (
    <>
      {/* Claude Design (8) Create Event Screen.dc.html:725-729: 14px/600
          headings and 13px helpers throughout the Tickets card. */}
      <SectionShell
        id="event-tickets"
        variant="design"
        className="!border-[#ECECEC] [&_h3]:text-[14px] [&_h3]:font-semibold [&>div:first-child>p]:mt-[4px] [&>div:first-child>p]:text-[13px]"
        title="Tickets"
        description="Create ticket types and pricing options for this event."
      >
        <FormControlLabel
          sx={{ alignItems: "flex-start", mx: 0 }}
          control={
            <Checkbox
              checked={Boolean(event.eventIsFree)}
              onChange={({ target }) => update("eventIsFree", target.checked)}
              sx={{ pt: 0 }}
            />
          }
          label={
            <div>
              <div className="text-[14px] leading-5 text-[#0A0A0A]">
                This event is free (no tickets required)
              </div>
            </div>
          }
        />

        {!event.eventIsFree ? (
          <>
            <div>
              <h3 className="text-[14px] font-semibold leading-tight text-[#0A0A0A]">
                Ticket Types
              </h3>
              <p className="mt-1 text-[13px] text-[#0A0A0A]">
                Click Add Ticket Type to create the ticket types and prices you want to offer attendees.
              </p>
              {/* Create Event Screen.dc.html:740: shown after Next/Save finds no ticket types. */}
              {errors.tickets && event.tickets.length === 0 ? (
                <p role="alert" className="mt-1 text-[12px] !text-[#F0202E]">
                  Add at least one ticket type or mark the event as free
                </p>
              ) : null}
              {/* Design: the table (and its header) appears once a ticket exists. */}
              {event.tickets.length > 0 ? <TableContainer className="mt-3 rounded-lg border border-gray-200">
                <Table sx={{ "& .MuiTableCell-root": { fontFamily: '"DM Sans", sans-serif', fontSize: 13, py: "14px" } }}>
                  <TableHead className="bg-gray-50">
                    <TableRow>
                      <TableCell className="!w-10" />
                      <TableCell className={ticketTableHeadCellClassName}>Code</TableCell>
                      <TableCell className={ticketTableHeadCellClassName}>Ticket Type</TableCell>
                      <TableCell className={ticketTableHeadCellClassName}>Price</TableCell>
                      <TableCell className={ticketTableHeadCellClassName} align="center">
                        Seats
                      </TableCell>
                      <TableCell className={ticketTableHeadCellClassName} align="center">Taxable</TableCell>
                      <TableCell className={ticketTableHeadCellClassName}>Availability Limit</TableCell>
                      <TableCell className={ticketTableHeadCellClassName}>Sales Period</TableCell>
                      <TableCell
                        className={`${ticketTableHeadCellClassName} !max-w-[170px]`}
                        align="center"
                      >
                        Visible on Registration Form
                      </TableCell>
                      <TableCell className={ticketTableHeadCellClassName}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {event.tickets.map((ticket, index) => {
                      const reorder = reorderTicketRow(index);
                      return (
                      <TableRow
                        key={ticket.id}
                        {...eventReorderTableRowProps(reorder.rowProps)}
                      >
                        <TableCell>
                          <EventReorderHandle {...reorder.handleProps} />
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-[#0A0A0A]">{ticket.code || ""}</span>
                        </TableCell>
                        <TableCell>
                          <div className="text-[13px] font-semibold text-[#0A0A0A]">
                            {ticket.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* POL-005: prices store canonically ("85.00"); the
                              spec's "$85.00" column display re-adds the "$". */}
                          <span className="font-bold text-black">
                            {formatDollars(ticket.price)}
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          <span className="font-extrabold text-black">
                            {Math.max(
                              1,
                              Math.floor(Number(ticket.attendeesPerTicket) || 1)
                            )}
                          </span>
                        </TableCell>
                        {/* Design Create Event Screen taxable: a No Tax ticket
                            type shows No. */}
                        <TableCell align="center">
                          {ticket.noTax && taxableLabel === "Yes" ? "No" : taxableLabel}
                        </TableCell>
                        <TableCell>
                          {ticket.availabilityLimit || "No limit"}
                        </TableCell>
                        <TableCell className="!max-w-[150px]">
                          {salesPeriodLabel(ticket)}
                        </TableCell>
                        <TableCell align="center">
                          <span
                            className={`text-[13px] font-extrabold uppercase ${
                              ticket.visibleOnRegistrationForm === false
                                ? "text-red-500"
                                : "text-emerald-500"
                            }`}
                          >
                            {ticket.visibleOnRegistrationForm === false ? "NO" : "YES"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <WizardActionButton
                              tone="gold"
                              size="small"
                              startIcon={<EditOutlinedIcon />}
                              onClick={() => openEdit(ticket)}
                            >
                              Edit
                            </WizardActionButton>
                            <WizardActionButton
                              tone="red"
                              size="small"
                              startIcon={<DeleteOutlineIcon />}
                              aria-label={`${ticket.persisted ? "Remove" : "Delete"} ${ticket.name}`}
                              disabled={ticket.persisted && ticket.visibleOnRegistrationForm === false}
                              onClick={() => setPendingDelete(ticket)}
                            >
                              {ticket.persisted ? "Remove" : "Delete"}
                            </WizardActionButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </TableContainer> : null}
              <WizardActionButton
                className="!mt-4"
                tone="gold"
                sx={{ minHeight: 38, fontSize: 13, borderRadius: "8px", px: "16px" }}
                startIcon={<AddIcon />}
                onClick={openAdd}
              >
                Add Ticket Type
              </WizardActionButton>
            </div>
          </>
        ) : null}
        {!event.eventIsFree ? <>
          {/* Create Event Screen.dc.html:771-787 */}
          <div className="flex flex-wrap items-start justify-between gap-[20px] border-t border-[#ECECEC] pt-[20px]">
            <div className="flex min-w-[280px] flex-1 flex-col gap-[4px]">
              <h3 className="text-[14px] font-semibold text-[#0A0A0A]">Enable Pre-Event Check-in</h3>
              <p className="text-[13px] text-[#0A0A0A]">Enable to allow, all registered attendees to receive an email in advance of the event with a link to confirm their attendance. Attendees who confirm through the link will be automatically marked as Pre-Checked-in under the Manage Event Attendees list, helping your team speed up check-in on the day of the event.</p>
            </div>
            <FormControlLabel sx={{ m: 0, gap: "10px", "& .MuiFormControlLabel-label": { fontSize: 12, fontWeight: 600, color: preCheckInOn ? "#10B981" : "#727272" } }} control={<Switch checked={preCheckInOn} onChange={e => update("preCheckInEnabled", e.target.checked)} inputProps={{ "aria-label": "Enable Pre-Event Check-in" }} />} label={preCheckInOn ? "ENABLED" : "DISABLED"} />
          </div>
          <div>
            <RadioGroup row aria-label="Pre-check-in email time" value={preCheckInOn && event.preCheckInHours ? String(event.preCheckInHours) : ""} onChange={e => update("preCheckInHours", Number(e.target.value))}
              className="gap-[40px]" sx={{ opacity: preCheckInOn ? 1 : 0.45, pointerEvents: preCheckInOn ? "auto" : "none", "& .MuiFormControlLabel-root": { m: 0, gap: "8px" }, "& .MuiRadio-root": { p: 0 }, "& .MuiFormControlLabel-label, & .MuiFormControlLabel-label.Mui-disabled": { fontSize: 14, color: "#0A0A0A" } }}>
              {[24, 48, 72].map(hours => <FormControlLabel key={hours} value={String(hours)} disabled={!preCheckInOn} control={<Radio />} label={`${hours} hrs prior`} />)}
            </RadioGroup>
            {errors.preCheckInHours && preCheckInWindowMissing ? <p role="alert" className="mt-[6px] text-[12px] !text-[#F0202E]">{errors.preCheckInHours}</p> : null}
          </div>
        </> : null}
        {/* Create Event Screen.dc.html:790-798 */}
        <div className="flex flex-col gap-[8px]">
          <h3 className="text-[14px] font-semibold text-[#0A0A0A]">Event Capacity <span className="font-normal text-[#727272]">(Optional)</span></h3>
          <p className="text-[13px] text-[#0A0A0A]">Set the total maximum number of attendees for this event.</p>
          <div className="flex items-center gap-[10px]">
            <TextField
              size="small"
              placeholder="00"
              value={event.capacity}
              inputProps={{ inputMode: "numeric", "aria-label": "Event Capacity" }}
              onChange={({ target }) => { if (/^\d*$/.test(target.value)) update("capacity", target.value); }}
              sx={{ ...wizardTextFieldSx, width: 120 }}
            />
            <span className="text-[14px] text-[#0A0A0A]">attendees</span>
          </div>
        </div>

        {/* Create Event Screen.dc.html:802-822 */}
        {!event.eventIsFree ? <div className="flex flex-col gap-[14px] rounded-[10px] border border-[#ECECEC] p-[22px]">
          <div className="flex flex-col gap-[2px]">
            <h4 className="text-[14px] font-semibold leading-tight text-[#0A0A0A]">
              Ticket Settings
            </h4>
            <p className="text-[13px] text-[#0A0A0A]">
              Select the options below that you would like to apply.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-[20px] md:grid-cols-3">
            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0 }}
              control={
                <Checkbox
                  checked={event.displayRemainingTicketQuantity ?? true}
                  onChange={({ target }) =>
                    update("displayRemainingTicketQuantity", target.checked)
                  }
                  // Event-wizard checkboxes are green (July 9, 2026 direction).
                  sx={{ pt: 0 }}
                />
              }
              label={
                <div>
                  <div className="text-[14px] font-semibold leading-5 text-[#0A0A0A]">
                    Display remaining ticket quantity
                  </div>
                  <div className="text-[13px] leading-5 text-[#0A0A0A]">
                    Show total of how many tickets are left
                  </div>
                </div>
              }
            />
            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0 }}
              control={
                <Checkbox
                  checked={Boolean(event.displayRemainingTicketTypeQuantity)}
                  onChange={({ target }) =>
                    update("displayRemainingTicketTypeQuantity", target.checked)
                  }
                  sx={{ pt: 0 }}
                />
              }
              label={
                <div>
                  <div className="text-[14px] font-semibold leading-5 text-[#0A0A0A]">
                    Display remaining ticket type quantity
                  </div>
                  <div className="text-[13px] leading-5 text-[#0A0A0A]">
                    Show how many tickets are left for each type.
                  </div>
                </div>
              }
            />
            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0 }}
              control={
                <Checkbox
                  checked={Boolean(event.allowWaitlist)}
                  onChange={({ target }) => update("allowWaitlist", target.checked)}
                  sx={{ pt: 0 }}
                />
              }
              label={
                <div>
                  <div className="text-[14px] font-semibold leading-5 text-[#0A0A0A]">
                    Allow waitlist when sold out
                  </div>
                  <div className="text-[13px] leading-5 text-[#0A0A0A]">
                    Attendees can join a waitlist if tickets sell out.
                  </div>
                </div>
              }
            />
          </div>
        </div> : null}
      </SectionShell>

      <ResultModal open={Boolean(saveResult)} title={saveResult || ""} onClose={() => setSaveResult(null)} />
      <ResultModal open={Boolean(deletedMessage)} title={deletedMessage?.message || ""} highlights={deletedMessage?.highlights} onClose={() => setDeletedMessage(null)} />
      {/* Add / Edit ticket dialog (Claude Design (8) Create Event Screen.dc.html:1318-1389) */}
      <Dialog open={Boolean(draft)} onClose={unsavedGuard.requestClose} disableEnforceFocus={unsavedGuard.promptOpen} maxWidth={false} fullWidth
        PaperProps={{ sx: { fontFamily: '"DM Sans", sans-serif', color: "#0A0A0A", "& button, & input, & .MuiTypography-root": { fontFamily: "inherit" }, "& .MuiButton-root": { fontWeight: 600 }, "& .font-extrabold": { fontWeight: 600, fontSize: 14 }, "& input": { fontFamily: "inherit", fontSize: 14 }, "& p": { color: "#0A0A0A" }, maxWidth: "clamp(600px, 72vw, 980px)", maxHeight: "92vh", borderRadius: "14px", mx: { xs: 2, sm: 4 }, width: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" } } }}>
        <DialogContent className="flex flex-col gap-[20px] !p-[20px] md:!p-[32px]">
          <WizardDialogHeader title={isNew ? "Add Ticket Type" : "Edit Ticket Type"} subtitle="Add the details for this ticket type. Tickets available exclusively for members should be entered as Member Only Ticket and will require attendees to select a member from the member list." onClose={unsavedGuard.requestClose} />
          {draft && <>
            <div className="grid grid-cols-1 gap-[20px] md:grid-cols-3 md:grid-rows-[auto_auto_auto_auto]">
              {/* A name saved with an explicit blank code ("") clears the code field;
                  a name with no saved pair (null) keeps the code already entered,
                  unless that code belongs to another name (EventLibrarySelect). */}
              <EventLibrarySelect chamberId={chamberId} kind={CmpEventLibraryKindEnum.TicketName} label="Ticket Type Name" required compact
                hint="Add New Ticket Name or select from the dropdown of premade and previously created tickets that appear on the registration form for attendees to select." placeholder="Select Premade Ticket Type Name" searchPlaceholder="Search Ticket Name"
                error={showValidation && draftNameMissing ? enterFieldMessage("Ticket Type Name") : undefined}
                value={draft.name} pairedValue={draft.code} attendeesPerTicket={Number(draft.attendeesPerTicket) || 1}
                onChange={(value, pair, seats) => patchDraft({ name: value, code: pair ?? draft.code, attendeesPerTicket: isNew && seats ? String(seats) : draft.attendeesPerTicket })} />
              <EventLibrarySelect chamberId={chamberId} kind={CmpEventLibraryKindEnum.TicketCode} label="Ticket Code" compact
                hint="Add New Ticket Code or select from the dropdown of premade and previously created ticket codes." placeholder="Select Premade Ticket Type Code" searchPlaceholder="Search Ticket Code"
                value={draft.code || ""} pairedValue={draft.name} attendeesPerTicket={Number(draft.attendeesPerTicket) || 1}
                onChange={(value, pair, seats) => patchDraft({ code: value, name: pair || draft.name, attendeesPerTicket: isNew && seats ? String(seats) : draft.attendeesPerTicket })} />
              <div className="grid grid-cols-[minmax(0,1fr)] content-start gap-[6px] md:row-span-4 md:grid-rows-subgrid"><FieldLabel compact className="contents" hintClassName="text-[13px] leading-[normal] text-[#0A0A0A]" hint="Set the price attendees will pay for this ticket.">Ticket Price</FieldLabel>
                <CurrencyInput ariaLabel="Ticket Price" placeholder="0.00" value={draft.price} onChange={value => patchDraft({ price: value })}
                  inputClassName="h-[42px] w-full rounded-md border border-[#9E9E9E] bg-white pl-8 pr-4 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100" adornmentClassName={wizardCurrencyAdornmentClassName} /></div>
            </div>
            {/* Claude Design (8) removed the "Members only" box (Create Event
                Screen.dc.html:1320-1389); saveDraft derives it from the name. */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-[max-content_minmax(0,1fr)]">
              <div><FieldLabel compact hint="Set if you want to limit the number of tickets available for the above ticket.">Availability Limit <span className="font-normal">(Optional)</span></FieldLabel>
                <div className="grid h-[42px] w-fit grid-cols-[40px_56px_40px] items-stretch overflow-hidden rounded-[8px] border border-[#9E9E9E]">
                  <button type="button" className="flex items-center justify-center border-r border-[#ECECEC] text-[16px] leading-none text-[#727272] hover:bg-[#F7F7F8]" aria-label="Decrease availability limit" onClick={() => setDraftLimit(Math.max(0, draftLimit - 1))}>−</button>
                  <input aria-label="Availability limit" inputMode="numeric" className="w-full min-w-0 bg-transparent text-center outline-none" value={draft.availabilityLimit || "0"}
                    onChange={({ target }) => { if (/^\d*$/.test(target.value)) patchDraft({ availabilityLimit: target.value }); }} />
                  <button type="button" className="flex items-center justify-center border-l border-[#ECECEC] text-[16px] leading-none text-[#727272] hover:bg-[#F7F7F8]" aria-label="Increase availability limit" onClick={() => setDraftLimit(draftLimit + 1)}>+</button>
                </div>
              </div>
              <div><FieldLabel compact hint="To restrict when this type of ticket will be available on the event page for attendees to purchase, select the sales period.">Sales Period <span className="font-normal">(Optional)</span></FieldLabel>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <CalendarDateInput mode="fullDate" appearance="claude" iconPosition="left" ariaLabel="Sales period start date" placeholder="Start Date"
                    value={draft.salesStartDate || ""} onChange={value => patchDraft({ salesStartDate: value })} />
                  <CalendarDateInput mode="fullDate" appearance="claude" iconPosition="left" ariaLabel="Sales period end date" placeholder="End Date"
                    value={draft.salesEndDate || ""} onChange={value => patchDraft({ salesEndDate: value })} />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-[8px]"><h4 className="m-0 text-[15px] font-bold text-[#0A0A0A]">Visible on Website Registration Form</h4>
              <p className="m-0 text-[13px]">All ticket types will be shown on the online registration form on your event page. Check the box below to keep this ticket type private for internal registration and use only.</p>
              <FormControlLabel className="!m-0 !mt-[6px] !items-start !gap-[12px]" sx={{ "& .MuiCheckbox-root": { p: 0, mt: "2px" } }} control={<Checkbox checked={draft.visibleOnRegistrationForm === false} onChange={(_, hidden) => patchDraft({ visibleOnRegistrationForm: !hidden })} />}
                label={<span className="flex flex-col gap-[2px]"><span className="text-[15px] font-semibold text-[#0A0A0A]">Select if you want to hide this ticket from showing on the registration form.</span><span className="text-[13px] text-[#0A0A0A]">This ticket will not be visible to attendees. This ticket can be invoiced through your {noun.singular} dashboard.</span></span>} />
            </div>
            {/* Approved Oct 16 2026: No Tax leaves this ticket type out of invoice
                tax (design noTax, Taxable column). Same style as the hide box. */}
            <FormControlLabel className="!m-0 !items-start !gap-[12px]" sx={{ "& .MuiCheckbox-root": { p: 0, mt: "2px" } }} control={<Checkbox checked={draft.noTax === true} onChange={(_, noTax) => patchDraft({ noTax })} />}
              label={<span className="text-[15px] font-semibold text-[#0A0A0A]">No Tax</span>} />
            <WizardActionButton tone="gold" sx={{ alignSelf: "flex-start", mt: "8px", minHeight: 0, px: "44px", py: "13px", fontSize: 14, borderRadius: "8px", lineHeight: "normal" }} onClick={saveDraft}>Save Ticket Type</WizardActionButton>
          </>}
        </DialogContent>
      </Dialog>
      {unsavedGuard.prompt}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        tone="danger"
        title={pendingDelete?.persisted ? "Remove this ticket from registration?" : "Delete this ticket type?"}
        message={pendingDelete?.persisted
          ? "This ticket will no longer be offered on the public registration form after you update the event. Existing registrations and payment history will be kept."
          : "This ticket type is not saved yet, so it will be removed from this form."}
        confirmLabel={pendingDelete?.persisted ? "Remove" : "Delete"}
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
};

export default Step3TicketSetup;
